import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, isDefined, ModalClass } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateFunctionalElementRemarks,
    updateFunctionalElementSelection,
    updateMaterialSwapIn,
    updateMaterialSwapOut,
    updateRepairTime,
    updateSerialSwapIn,
    updateSerialSwapOut,
} from '../../store/actions/functional-elements.actions';
import {
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
    getOptionPresentNoSelectedForFunctionalElement,
    isCosimaFunctionalElementFromTrainingChecklistReadOnly,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Choice, CHOICE_TYPE, Choices } from '../../types/choice';
import { ContentId } from '../../types/content-block';
import { DefectiveComponentReplacement } from '../../types/defective-component-replacement';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopyId } from '../../types/participant-copy';
import { isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { FormsDurationManualInputComponent } from './forms-duration-manual-input/forms-duration-manual-input.component';

const repairTimeDelta = 10; // incr ease or decrease by 10 minutes using the stepper buttons
@Component({
    selector: 'pace-forms-defective-component-replacement',
    templateUrl: './defective-component-replacement.component.html',
    styleUrls: ['./defective-component-replacement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefectiveComponentReplacementComponent implements OnInit, OnDestroy {
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public visible$: Observable<boolean>;
    public readOnly$: Observable<boolean>;
    public defectiveComponentReplacementFunctionalElement$: Observable<DefectiveComponentReplacement>;
    public choices$: Observable<Choices>;
    public displayInputFields$: Observable<boolean>;
    public selectedChoice$: Observable<Choice | undefined>;
    public negativeChoiceSelected$: Observable<boolean>;
    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public elementTypeLabel$: Observable<string>;
    public selectedParticipant$: Observable<ParticipantCopyId>;
    public showRemarksField$: Observable<boolean>;
    public optionPresentNoSelected$: Observable<boolean>;
    public disableRepairTimeButtons$: Observable<boolean>;
    public repairTimeClickTrigger$: Subject<void>;
    public repairTimeIncTrigger$: Subject<void>;
    public repairTimeDecTrigger$: Subject<void>;

    private destroyTrigger$: Subject<void>;
    public defectiveComponentReplacementFunctionalElementGroup: FormGroup;
    public radioGroup: FormGroup;
    public radioControl: FormControl;
    public remarksControl: FormControl;
    public materialSwapInControl: FormControl;
    public serialSwapInControl: FormControl;
    public materialSwapOutControl: FormControl;
    public serialSwapOutControl: FormControl;
    public repairTimeControl: FormControl;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
        private readonly store: Store<FormsState>,
        private readonly modalService: BsModalService,
    ) {
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
        this.destroyTrigger$ = new Subject<void>();
        this.repairTimeIncTrigger$ = new Subject<void>();
        this.repairTimeDecTrigger$ = new Subject<void>();
        this.repairTimeClickTrigger$ = new Subject<void>();
    }

    public ngOnInit(): void {
        this.materialSwapInControl = this.formBuilder.control('');
        this.serialSwapInControl = this.formBuilder.control('');
        this.materialSwapOutControl = this.formBuilder.control('');
        this.serialSwapOutControl = this.formBuilder.control('');
        this.repairTimeControl = this.formBuilder.control('');

        this.radioControl = this.formBuilder.control('');
        this.remarksControl = this.formBuilder.control('');
        this.radioGroup = this.formBuilder.group({ radioControl: this.radioControl });
        this.defectiveComponentReplacementFunctionalElementGroup = this.formBuilder.group({
            remarksControl: this.remarksControl,
            radioGroup: this.radioGroup,
            materialSwapIn: this.materialSwapInControl,
            serialSwapIn: this.serialSwapInControl,
            materialSwapOut: this.materialSwapOutControl,
            serialSwapOut: this.serialSwapOutControl,
        });

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.defectiveComponentReplacementFunctionalElementGroup);
                })
                .catch((error: Error) => {
                    this.log.error(
                        'DefectiveComponentReplacementComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    );
                });
        }

        const optionPresentNoSelectedSelector = getOptionPresentNoSelectedForFunctionalElement({ contentId: this.id });
        this.optionPresentNoSelected$ = this.store.select(optionPresentNoSelectedSelector);

        this.defectiveComponentReplacementFunctionalElement$ = this.store
            .select(getFormFunctionalElementByIdFactory<DefectiveComponentReplacement>({ id: this.id }))
            .pipe(filter(isDefined));

        this.readOnly$ = combineLatest([
            this.store.select(getFormParticipantCopyReadonly),
            this.store.select(isCosimaFunctionalElementFromTrainingChecklistReadOnly({ id: this.id })),
        ]).pipe(map(([readOnly, isCosimaReadOnly]) => readOnly || isCosimaReadOnly));

        this.elementTypeLabel$ = combineLatest([
            this.defectiveComponentReplacementFunctionalElement$.pipe(
                map((s) => s.elementType),
                filter(isDefined),
            ),
            this.store.select(getFormTypeFilters),
        ]).pipe(map(([elementType, filters]) => FormUtilities.getFilterLabel(elementType, filters)));

        this.showRemarksField$ = this.defectiveComponentReplacementFunctionalElement$.pipe(
            map((s) => !!(s.remarks?.length || s.choices?.choiceNegative?.selected)),
        );

        this.choices$ = this.defectiveComponentReplacementFunctionalElement$.pipe(
            map((s) => s.choices),
            filter(isDefined),
        );

        this.selectedChoice$ = this.choices$.pipe(
            map((choices) =>
                Object.values(choices)
                    .filter(isDefined)
                    .find((c) => c.selected),
            ),
        );

        this.displayInputFields$ = this.defectiveComponentReplacementFunctionalElement$.pipe(
            map(
                (functionalElement) =>
                    !!(
                        functionalElement.choices?.choiceNegative?.selected ||
                        functionalElement.remarks?.length ||
                        functionalElement.swapOutComponent.matNo.length > 0 ||
                        functionalElement.swapOutComponent.serialNo.length > 0 ||
                        functionalElement.swapInComponent.matNo.length > 0 ||
                        functionalElement.swapInComponent.serialNo.length > 0 ||
                        functionalElement.repairTime.length > 0
                    ),
            ),
        );

        this.negativeChoiceSelected$ = this.selectedChoice$.pipe(map((c) => c?.type === CHOICE_TYPE.CHOICE_NEGATIVE));

        this.visible$ = combineLatest([
            this.defectiveComponentReplacementFunctionalElement$,
            this.choices$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(
                ([
                    defectiveComponentReplacementFunctionalElement,
                    choices,
                    typeFilters,
                    formFilters,
                    selectedParticipant,
                ]) => {
                    const choicesValues = Object.values(choices);

                    // defe ctive functional elements without choices do not make sense, hence we will not display them.
                    if (choicesValues.length === 0) {
                        return false;
                    }

                    const isMasterCopy = selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID;

                    // in p articipant copies, only content that was answered with "Yes" in this session is displayed
                    const selectedInMaster = choicesValues.filter(isDefined).find((c) => c.selectedInMaster);
                    const yesInMaster =
                        typeof selectedInMaster !== 'undefined' &&
                        selectedInMaster.type === CHOICE_TYPE.CHOICE_POSITIVE;
                    const visibleInParticipantCopy =
                        !defectiveComponentReplacementFunctionalElement.contentLoadedFromXml && yesInMaster;

                    // filt ers apply both to master and participant copies
                    const visibleByFilter = isCosimaFunctionalElementVisible(
                        defectiveComponentReplacementFunctionalElement,
                        typeFilters,
                        formFilters,
                        defectiveComponentReplacementFunctionalElement.elementType,
                    );

                    return (isMasterCopy || visibleInParticipantCopy) && visibleByFilter;
                },
            ),
        );

        this.disableRepairTimeButtons$ = combineLatest([
            this.defectiveComponentReplacementFunctionalElement$,
            this.readOnly$,
        ]).pipe(
            map(
                ([defectiveComponentReplacementFunctionalElement, readOnly]) =>
                    defectiveComponentReplacementFunctionalElement.disabled || readOnly,
            ),
        );

        combineLatest([
            this.defectiveComponentReplacementFunctionalElement$.pipe(distinctUntilChanged()),
            this.readOnly$.pipe(distinctUntilChanged()),
        ])
            .pipe(
                switchMap(([defectiveComponentReplacementFunctionalElement, readOnly]) =>
                    combineLatest([
                        this.selectedChoice$.pipe(distinctUntilChanged()),
                        this.optionPresentNoSelected$.pipe(distinctUntilChanged()),
                        this.visible$.pipe(distinctUntilChanged()),
                    ]).pipe(
                        map(([selectedChoice, optionPresentNoSelected]) => ({
                            defectiveComponentReplacementFunctionalElement,
                            readOnly,
                            selectedChoice,
                            optionPresentNoSelected,
                        })),
                    ),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(
                ({
                    defectiveComponentReplacementFunctionalElement,
                    readOnly,
                    selectedChoice,
                    optionPresentNoSelected,
                }) => {
                    if (isDefined(selectedChoice)) {
                        this.radioControl.patchValue(selectedChoice.xmlId);
                    } else {
                        this.radioControl.reset('');
                    }
                    this.radioControl[optionPresentNoSelected || readOnly ? 'disable' : 'enable']({
                        emitEvent: false,
                    });

                    this.materialSwapInControl[
                        defectiveComponentReplacementFunctionalElement.disabled || readOnly ? 'disable' : 'enable'
                    ]({ emitEvent: false });
                    this.materialSwapInControl.patchValue(
                        defectiveComponentReplacementFunctionalElement.swapInComponent.matNo,
                    );

                    this.repairTimeControl[
                        defectiveComponentReplacementFunctionalElement.disabled || readOnly ? 'disable' : 'enable'
                    ]({ emitEvent: false });
                    this.repairTimeControl.patchValue(defectiveComponentReplacementFunctionalElement.repairTime);

                    this.serialSwapInControl[
                        defectiveComponentReplacementFunctionalElement.disabled || readOnly ? 'disable' : 'enable'
                    ]({ emitEvent: false });
                    this.serialSwapInControl.patchValue(
                        defectiveComponentReplacementFunctionalElement.swapInComponent.serialNo,
                    );

                    this.materialSwapOutControl[
                        defectiveComponentReplacementFunctionalElement.disabled || readOnly ? 'disable' : 'enable'
                    ]({ emitEvent: false });
                    this.materialSwapOutControl.patchValue(
                        defectiveComponentReplacementFunctionalElement.swapOutComponent.matNo,
                    );

                    this.serialSwapOutControl[
                        defectiveComponentReplacementFunctionalElement.disabled || readOnly ? 'disable' : 'enable'
                    ]({ emitEvent: false });
                    this.serialSwapOutControl.patchValue(
                        defectiveComponentReplacementFunctionalElement.swapOutComponent.serialNo,
                    );

                    this.remarksControl.patchValue(defectiveComponentReplacementFunctionalElement.remarks);
                    this.remarksControl[
                        !readOnly && selectedChoice?.type === CHOICE_TYPE.CHOICE_NEGATIVE ? 'enable' : 'disable'
                    ]({
                        emitEvent: false,
                    });

                    this.repairTimeControl.patchValue(defectiveComponentReplacementFunctionalElement.repairTime, {
                        emitEvent: false,
                    });
                },
            );

        this.radioControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(
                ([value, defectiveComponentReplacementFunctionalElement]: [
                    string | null,
                    DefectiveComponentReplacement,
                ]) => {
                    this.store.dispatch(
                        updateFunctionalElementSelection({
                            id: defectiveComponentReplacementFunctionalElement.id,

                            selectedChoiceXmlId: value === null ? undefined : value,
                        }),
                    );
                },
            );

        this.materialSwapInControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of defectiveComponentReplacementFunctionalElement$
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectiveComponentReplacement]: [string, DefectiveComponentReplacement]) => {
                this.store.dispatch(
                    updateMaterialSwapIn({
                        xmlId: defectiveComponentReplacement.swapInComponent.matNoXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.serialSwapInControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of defectiveComponentReplacementFunctionalElement$
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectiveComponentReplacement]: [string, DefectiveComponentReplacement]) => {
                this.store.dispatch(
                    updateSerialSwapIn({
                        xmlId: defectiveComponentReplacement.swapInComponent.serialNoXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.materialSwapOutControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of defectiveComponentReplacementFunctionalElement$
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectiveComponentReplacement]: [string, DefectiveComponentReplacement]) => {
                this.store.dispatch(
                    updateMaterialSwapOut({
                        xmlId: defectiveComponentReplacement.swapOutComponent.matNoXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.serialSwapOutControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of defectiveComponentReplacementFunctionalElement$
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectiveComponentReplacement]: [string, DefectiveComponentReplacement]) => {
                this.store.dispatch(
                    updateSerialSwapOut({
                        xmlId: defectiveComponentReplacement.swapOutComponent.serialNoXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.repairTimeControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of defectiveComponentReplacementFunctionalElement$
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectiveComponentReplacement]: [string, DefectiveComponentReplacement]) => {
                this.store.dispatch(
                    updateRepairTime({
                        xmlId: defectiveComponentReplacement.repairTimeXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.remarksControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(
                ([newValue, defectiveComponentReplacementFunctionalElement]: [
                    string,
                    DefectiveComponentReplacement,
                ]) => {
                    if (!isDefined(defectiveComponentReplacementFunctionalElement.xmlId)) {
                        throw new Error(
                            `DefectiveComponentReplacementComponent: remarksControl valueChanges: Functional element has no xml Id!`,
                        );
                    }
                    if (defectiveComponentReplacementFunctionalElement.remarksXmlId) {
                        this.store.dispatch(
                            updateFunctionalElementRemarks({
                                id: defectiveComponentReplacementFunctionalElement.id,
                                // usin g ! because remarksXmlId is guaranteed to be there in Cosima. It is only undefined in Astoria.
                                xmlId: defectiveComponentReplacementFunctionalElement.remarksXmlId,
                                newValue,
                            }),
                        );
                    }
                },
            );

        this.repairTimeIncTrigger$
            .asObservable()
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                switchMap(() => this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe((defectiveComponentReplacement) => {
                const repairTime = moment.duration(defectiveComponentReplacement.repairTime);
                repairTime.add(repairTimeDelta, 'minutes');
                this.store.dispatch(
                    updateRepairTime({
                        xmlId: defectiveComponentReplacement.repairTimeXmlId,
                        id: this.id,
                        newValue: repairTime.format('HH:mm', { trim: false }),
                    }),
                );
            });

        this.repairTimeClickTrigger$
            .asObservable()
            .pipe(
                switchMap(() => this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                exhaustMap((defectiveComponentReplacement) => {
                    const defectiveComponentXmlId = defectiveComponentReplacement.repairTimeXmlId;
                    if (!isDefined(defectiveComponentXmlId)) {
                        throw new Error(
                            `DefectiveComponentReplacementComponent: repairTimeClickTrigger$: Functional element has no xml Id!`,
                        );
                    }

                    const modalInstance = this.modalService.show(FormsDurationManualInputComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            duration: defectiveComponentReplacement.repairTime,
                        },
                        ariaLabelledBy: 'forms-duration-manual-input-modal-title',
                    });

                    return (
                        modalInstance.content?.modelChanged.asObservable().pipe(
                            takeUntil(this.modalService.onHide),
                            tap((duration) => {
                                this.store.dispatch(
                                    updateRepairTime({
                                        id: this.id,
                                        newValue: duration,
                                        xmlId: defectiveComponentXmlId,
                                    }),
                                );
                            }),
                        ) ?? of(undefined)
                    );
                }),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe();

        this.repairTimeDecTrigger$
            .asObservable()
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                switchMap(() => this.defectiveComponentReplacementFunctionalElement$.pipe(take(1))),
                distinctUntilChanged(),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe((defectiveComponentReplacement) => {
                const repairTime = moment.duration(defectiveComponentReplacement.repairTime);
                repairTime.subtract(repairTimeDelta, 'minutes');

                // ensu re that duration does not become negative
                if (repairTime.asSeconds() < 0) {
                    return;
                }

                this.store.dispatch(
                    updateRepairTime({
                        xmlId: defectiveComponentReplacement.repairTimeXmlId,
                        id: this.id,
                        newValue: repairTime.format('HH:mm', { trim: false }),
                    }),
                );
            });
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.radioGroup);
            this.parentForm.removeAt(index);
        }

        this.destroyTrigger$.next();
    }

    public reset(): void {
        this.radioControl.reset();
    }
}
